/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { addToArray } from '../../../utils';
import { defaultNewPage, selectCreatorMode, selectDocumentVersionById, setMessageFlipped } from '../../chatSlice';
import { useMessageIndex, usePageIfExists } from '../../hooks/MessageContext';
import styles from '../../Message.module.scss';
import { PageTypeEnum, PageType, DocumentVersionType } from '../../../algorithm/types';
import { RenderedHTML } from '../RenderedHtml/RenderedHTML';
import { TooltipIcon } from '../TooltipIcon';
import { InnerMessageHeader } from './InnerMessageHeader';
import { Page } from './Page';
import { pagePlaceholderContentInCreator, pageTypeAllowsContent } from '../../../algorithm/runAlgorithm';
import { usePageChrome } from '../../hooks/PageChromeContext';
import { DocumentTocGuidePage } from './DocumentTocGuidePage';
import { COMBINED_GUIDE_PAGE_ID } from './documentTocGuidePageConstants';
import { InnerMessageFooter } from './InnerMessageFooter';
import { DYNAMED_PAGE_ID } from '../../../DynaMed/dynamed';
import { DynamedTabContentPage } from '../../../DynaMed/DynamedTabContentPage';

/* Declare InnerMessageFlippedProps here so that we can set the correct type of InnerMessageFlipped below */
export type InnerMessageFlippedProps = {
  document_version_id?: string;
  onKeyToggled: (page: PageType, key: string, value: string) => void;
  loopCheckPageTitles?: string[];
  loopCheckPageTitlesNextPage?: string; // To be added to loopCheckPageTitles
  validationErrors?: {
    name: string | undefined;
    next_link: string | undefined;
    questions: (string | undefined)[] | undefined;
    true_link: string | undefined;
    false_link: string | undefined;
    fallback_link: string | undefined;
    calculator: { formulaBins?: { link?: string }[] } | undefined;
  };
  readOnly: boolean;
  readOnlyForContextualization: boolean;
  messageIndex: number;
};

export type InnerMessageProps = {
  document_version_id?: string;
  overrideContent?: JSX.Element | string;
  onKeyToggled: (page: PageType, key: string, value: string) => void;
  onShowTooltip: (element: HTMLElement, message: string | undefined, header: string | undefined) => void;
  allowEditing?: boolean;
  loopCheckPageTitles?: string[]; // A list of pages shown in this message, to check for loops
  loopCheckPageTitlesNextPage?: string; // To be added to loopCheckPageTitles
};

/* This renders the content of a page "chat bubble".  This a separate component because a single "chat bubble" might contain multiple
   pages - for example in a tabbed view */
export function InnerMessage({
  document_version_id: document_version_id_,
  overrideContent,
  onShowTooltip,
  onKeyToggled,
  allowEditing,
  loopCheckPageTitles: loopCheckPageTitles_,
  loopCheckPageTitlesNextPage,
}: InnerMessageProps) {
  const { page, page_name } = usePageIfExists() || {};
  const messageIndex = useMessageIndex();
  const document_version_id = page ? page.document_version_id : document_version_id_;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, document_version_id));
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const patient_notes_summary = useAppSelector((state) => state.user.pearlUserInfo?.userFlags?.patient_notes_summary);
  const isChat = useMatch('/chat');
  const loopCheckPageTitles = useMemo(
    () => addToArray(loopCheckPageTitles_, loopCheckPageTitlesNextPage),
    [loopCheckPageTitlesNextPage, loopCheckPageTitles_]
  );

  const dispatch = useAppDispatch();

  const handleSetFlipped = useCallback(
    () => {
      dispatch(setMessageFlipped({ messageIndex, page }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageIndex, page]
  );
  const handleSetFlippedForPage = useCallback(
    (pageToEdit: PageType) => {
      dispatch(setMessageFlipped({ messageIndex, page: pageToEdit }));
    },
    [dispatch, messageIndex]
  );

  if (!page && page_name && !overrideContent) {
    if (page_name.startsWith('#')) {
      // An external link - this is an internal error to have gotten to this with an external link
      throw new Error(`External link passed to InnerMessage: ${page_name}`);
    }
    if (allowEditing && document_version_id) {
      const handleClicked = () => {
        if (document_version_id) {
          dispatch(setMessageFlipped({ messageIndex, page: defaultNewPage(false, document_version_id!, page_name) }));
        }
      };
      return (
        <div className={styles.pageErrorClickable} onClick={handleClicked}>
          Click to create missing page '{page_name}'
        </div>
      );
    }
    // Note our urls are like:  #documentname#pagename
    // so to link to just a page use ##pagename
    return (
      <div className={styles.pageError}>
        Page '{isChat ? page_name : <a href={`##${encodeURI(page_name)}`}>{page_name}</a>}' missing
      </div>
    );
  }
  // When a page is included in another page, we add 0.1 to the messageIndex,
  // so this is an easy way to check if we are in a page that is included in another page,
  // so we don't show the header twice
  const isPageInPage = Math.round(messageIndex) !== messageIndex;

  if (page && loopCheckPageTitles?.includes(page.name)) {
    return <div className={styles.pageError}>Include loops not allowed</div>;
  }

  const tooltipComponent = page?.tooltip && (
    <TooltipIcon
      onTooltipClick={onShowTooltip}
      pageId={page?.id}
      tooltip={page?.tooltip || ''}
      className={styles.tooltipicon}
    />
  );
  const shouldRenderPageHeader = page?.pageType !== PageTypeEnum.GUIDE && !isPageInPage && page?.id !== 'LANDING';
  const shouldShowHeaderChrome = Boolean(allowEditing && page && page.pageType !== PageTypeEnum.TABS);
  const headerChrome =
    shouldShowHeaderChrome && page ? (
      <PageHeaderChrome page={page} documentVersion={documentVersion} handleSetFlipped={handleSetFlipped} />
    ) : undefined;
  const shouldRenderChromeOnlyHeader = Boolean(!shouldRenderPageHeader && headerChrome && page?.id !== 'LANDING');

  if (page?.id === COMBINED_GUIDE_PAGE_ID && documentVersion) {
    return (
      <>
        <InnerMessageHeader isFlipped={false} documentVersion={documentVersion} isRoot={true} page={page} />
        <DocumentTocGuidePage
          allowEditing={allowEditing}
          documentVersion={documentVersion}
          onEditPage={handleSetFlippedForPage}
        />
        <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />
      </>
    );
  }

  if (page?.id === DYNAMED_PAGE_ID && documentVersion) {
    return (
      <>
        <InnerMessageHeader isFlipped={false} documentVersion={documentVersion} isRoot={true} page={page} />
        <DynamedTabContentPage
          allowEditing={allowEditing}
          documentId={documentVersion.document?.id}
          documentSlug={documentVersion.document?.slug}
          documentVersionId={documentVersion.id}
          guideName={documentVersion.title}
          pageId={page.id}
        />
        <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />
      </>
    );
  }

  return (
    <>
      {shouldRenderPageHeader ? (
        <InnerMessageHeader
          isFlipped={false}
          documentVersion={documentVersion}
          tooltipComponent={tooltipComponent}
          isRoot={page?.isRoot}
          page={page}
          headerChrome={headerChrome}
        />
      ) : shouldRenderChromeOnlyHeader ? (
        <InnerMessageHeader
          isFlipped={false}
          documentVersion={documentVersion}
          tooltipComponent={tooltipComponent}
          isRoot={false}
          page={page}
          headerChrome={headerChrome}
        />
      ) : tooltipComponent ? (
        <div className={styles.tooltipiconDiv}>{tooltipComponent}</div>
      ) : undefined}

      {(overrideContent || page?.content || allowEditing) /* show pageContent even if empty but it's flippable */ &&
        (pageTypeAllowsContent(page?.pageType) || (allowEditing && pagePlaceholderContentInCreator(page))) &&
        (overrideContent != null && typeof overrideContent !== 'string' ? (
          overrideContent
        ) : (
          <>
            <RenderedHTML
              className={`${styles.pageContent} ${
                !isCreatorMode && patient_notes_summary ? styles.gapForCopyButton : ''
              }`}
              content={overrideContent || (pageTypeAllowsContent(page?.pageType) ? page?.content : '') || ''}
              documentId={documentVersion?.document?.id}
              documentVersionId={page?.document_version_id}
              documentSlug={documentVersion?.document?.slug}
              pageId={page?.id}
              pageName={page?.name}
              placeholder={allowEditing ? pagePlaceholderContentInCreator(page) : undefined}
              creatorMode={!!allowEditing}
            />
          </>
        ))}
      {page && (
        /* Note that a page will itself include InnerMessages */
        <Page
          onKeyToggled={onKeyToggled}
          onShowTooltip={onShowTooltip}
          allowEditing={allowEditing}
          loopCheckPageTitles={loopCheckPageTitles}
          readOnly
          readOnlyForContextualization
        />
      )}
    </>
  );
}

interface EditBadgesProps {
  page: PageType;
  documentVersion?: DocumentVersionType;
  handleSetFlipped: () => void;
}

export function PageHeaderChrome({ page, documentVersion, handleSetFlipped }: EditBadgesProps) {
  const { renderHeaderChrome, renderHeaderExtras } = usePageChrome();

  if (renderHeaderChrome) {
    return <>{renderHeaderChrome(page, documentVersion, handleSetFlipped)}</>;
  }

  const headerExtras = renderHeaderExtras?.(page);

  return <>{headerExtras}</>;
}

export function EditBadges({ page, documentVersion, handleSetFlipped }: EditBadgesProps) {
  return (
    <div className={styles.editBadgeContainer}>
      <div className={styles.editBadgeContainerInner}>
        <PageHeaderChrome page={page} documentVersion={documentVersion} handleSetFlipped={handleSetFlipped} />
      </div>
    </div>
  );
}
